import {
  Alert,
  Box,
  Button,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  Link,
  NativeSelect,
  RadioGroup,
  Separator,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../shared/stores/RootStore";
import useAutoResizeMap from "../../../hooks/useAutoResizeMap";
import { useEffect } from "react";
import {
  ArrowBackIcon,
  ArrowRightIcon,
  CheckIcon,
  SaveIcon,
} from "../../../shared/views/icons";
import { t } from "i18next";
import { toast } from "../../../shared/views/CustomToaster";
import { HlaWayDetailDelete } from "./HlaWayDetailDelete";
import { MergeIntoMenu } from "./MergeIntoMenu";
import { CrossSectionView } from "./cross-section/CrossSectionView";
import { FacilityType, HeavyVehicleFlowType } from "@keita/lanepatrol-sdk";
import { MultiSelectionWarning } from "./form/MultiSelectionWarning";
import { NumberField } from "./form/NumberField";
import { FieldWrapper } from "./form/FieldWrapper";
import { CreateOrUpdateHlaLane } from "../../models/HlaLane";
import { HlaLaneForm } from "./form/HlaLaneForm";
import { RadioOptions } from "./form/RadioOptions";
import { NotApplicable } from "./form/NotApplicable";
import { hlaWayToUpdate, HlaWayType } from "../../models/HlaWay";
import { NewLaneButton } from "./NewLaneButton";
import turf from "../../../shared/utils/turf";
import { CutHlaModal } from "./cut/CutHlaModal";
import { hlaAttributeOptions } from "../../../map/models/HlaAttribute";
import { MapMode } from "../../../project/stores/MapStore";

const HlaWayDetailBase = () => {
  useAutoResizeMap();
  const {
    mapStore,
    hlaStore,
    projectStore,
    filterStore,
    streetViewStore,
    hlaForecastStore,
  } = useStores();
  const cutHlaModal = useDisclosure();
  const hlaWay = hlaStore.getSelected()!;
  const project = projectStore.getSelected();
  const noPermissions = !project?.permissions?.canCodeHla;
  const canEdit = !noPermissions && !hlaForecastStore.isForecastModeActivated();
  const selectedLane = hlaStore.getSelectedLane();
  const form = hlaStore.form;

  useEffect(() => {
    mapStore.setMode(MapMode.HlaCoding);
    return () => {
      mapStore.setMode(MapMode.ProjectPage);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    hlaStore.setForm(hlaWayToUpdate(hlaWay));
    // eslint-disable-next-line
  }, [hlaWay]);

  useEffect(() => {
    return () => {
      if (!hlaStore.changesSaved) {
        submit();
      }
    };
    // eslint-disable-next-line
  }, [hlaWay.id]);

  const submit = () => {
    hlaStore.verify();
    hlaStore
      .saveChanges()
      .then(() => {
        toast("success", `Saved`);
      })
      .catch(() => {
        toast("error", `Failed to save`);
      });
  };

  const laneRequiresRoadFields = (lane: CreateOrUpdateHlaLane | null) => {
    if (!lane) return false;
    const isRoad =
      lane.facilityType === FacilityType.OnRoadBicycleLane ||
      lane.facilityType === FacilityType.RoadShoulder ||
      lane.facilityType === FacilityType.MixedTrafficRoadLane;
    const isAdjacentToRoad =
      (lane.adjacentFeaturesLeft.roadLane != null &&
        !lane.adjacentFeaturesLeft.hardSegregation) ||
      (lane.adjacentFeaturesRight.roadLane != null &&
        !lane.adjacentFeaturesRight.hardSegregation);
    return isRoad || isAdjacentToRoad;
  };

  const roadFieldsRequired =
    laneRequiresRoadFields(form.frontFacingLane) ||
    laneRequiresRoadFields(form.backFacingLane);

  const unverifiedWays = filterStore.filteredHlaWays.filter(
    (way) => !way.verified,
  );

  return (
    <Stack py={2} overflow={"auto"} w={"full"}>
      {/* Header */}
      <HStack px={6}>
        <Box flexShrink={0} width="40px">
          <IconButton
            size="sm"
            variant="ghost"
            onClick={() => {
              hlaStore.clearSelected();
            }}
          >
            <Icon as={ArrowBackIcon} />
          </IconButton>
        </Box>
        <Text px={2} fontWeight="semibold">
          {hlaWay.name ?? "Unknown street"}
        </Text>

        {canEdit && <HlaWayDetailDelete />}
        {canEdit && (
          <Button
            onClick={cutHlaModal.onOpen}
            variant={"plain"}
            size={"sm"}
            colorPalette={"primary"}
            fontSize={"sm"}
          >
            Cut...
          </Button>
        )}
        {canEdit && <MergeIntoMenu />}

        <Spacer />

        <HStack fontSize="sm" gap={4}>
          {canEdit && (
            <Button
              colorPalette={"primary"}
              size={"sm"}
              fontSize="sm"
              onClick={submit}
              loading={hlaStore.savingChanges}
              disabled={hlaStore.changesSaved && form.verified}
            >
              <SaveIcon />{" "}
              {!form.verified && hlaStore.changesSaved ? "Verify" : "Save"}
            </Button>
          )}

          {unverifiedWays.length === 0 ? (
            <HStack fontSize={"xs"} color="gray.500">
              <CheckIcon /> All verified
            </HStack>
          ) : (
            <Button
              colorPalette={"primary"}
              size={"sm"}
              fontSize="sm"
              disabled={!form.verified}
              onClick={async () => {
                const lastPosition =
                  hlaWay.coordinates[hlaWay.coordinates.length - 1];
                if (unverifiedWays.length === 0) {
                  hlaStore.clearSelected();
                  return;
                }

                const waysAndDistanceToLastPoint = unverifiedWays.map(
                  (way) => ({
                    way,
                    distance: turf.distance(lastPosition, way.coordinates[0]),
                  }),
                );
                const sortedWays = waysAndDistanceToLastPoint
                  .toSorted((a, b) => a.distance - b.distance)
                  .map((x) => x.way);

                hlaStore.select(sortedWays[0].id);
                streetViewStore.goToHla(sortedWays[0]);
              }}
            >
              <ArrowRightIcon /> Next
            </Button>
          )}
        </HStack>
      </HStack>

      <MultiSelectionWarning />

      {noPermissions && (
        <Stack px={6}>
          <Alert.Root status={"info"}>
            <Alert.Indicator />
            <Alert.Description>
              You don't have permission to manage high-level assessments in this
              project
            </Alert.Description>
          </Alert.Root>
        </Stack>
      )}

      <Stack gap={6}>
        <Stack px={6}>
          <Text textStyle={"lg"}>Road</Text>
          <SimpleGrid columns={{ xl: 1, "2xl": 2 }} gap={6}>
            <FieldWrapper label={t(`shared:hlaAttribute.type`)}>
              <NativeSelect.Root
                size="sm"
                fontSize={"sm"}
                disabled={!canEdit}
                invalid={form.type == null}
              >
                <NativeSelect.Field
                  value={form.type}
                  onChange={(e) => {
                    const value = e.target.value;
                    hlaStore.code((prev) => ({
                      ...prev,
                      type: value as HlaWayType,
                    }));
                  }}
                >
                  {hlaAttributeOptions.type!.map((option) => (
                    <option value={option.value} key={option.value}>
                      {t(option.translation)}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </FieldWrapper>

            <FieldWrapper label={t(`shared:hlaAttribute.heavyVehicleFlow`)}>
              {roadFieldsRequired ? (
                <RadioGroup.Root
                  colorPalette={"primary"}
                  disabled={!canEdit}
                  value={form.heavyVehicleFlow}
                  onValueChange={({ value }) => {
                    hlaStore.code((prev) => ({
                      ...prev,
                      heavyVehicleFlow: value as HeavyVehicleFlowType,
                    }));
                  }}
                >
                  <RadioOptions
                    options={hlaAttributeOptions.heavyVehicleFlow!}
                  />
                </RadioGroup.Root>
              ) : (
                <NotApplicable />
              )}
            </FieldWrapper>

            <FieldWrapper
              label={t("shared:hlaAttribute.averageRoadSpeed", {
                speedUnit: projectStore.speedUnitStr,
              })}
            >
              <NumberField
                step={10}
                disabled={!canEdit}
                onChange={(value) => {
                  hlaStore.code((prev) => ({
                    ...prev,
                    averageRoadSpeed: value,
                  }));
                }}
                value={form.averageRoadSpeed}
                notApplicable={!roadFieldsRequired}
              />
              {form.averageRoadSpeed == null && (
                <Link
                  fontSize="xs"
                  onClick={() => {
                    projectStore.setShowProjectDefaultsModal(true);
                  }}
                >
                  {`Value defaults to ${projectStore.getDefaultAverageRoadSpeed(
                    form.type,
                  )} for road type "${t(`shared:hlaWayType.${form.type}`)}"`}
                </Link>
              )}
            </FieldWrapper>

            <FieldWrapper label={t(`shared:hlaAttribute.averageDailyTraffic`)}>
              <NumberField
                step={1000}
                disabled={!canEdit}
                onChange={(value) => {
                  hlaStore.code((prev) => ({
                    ...prev,
                    averageDailyTraffic: value,
                  }));
                }}
                value={form.averageDailyTraffic}
                notApplicable={!roadFieldsRequired}
              />
              {form.averageDailyTraffic == null && (
                <Link
                  fontSize="xs"
                  onClick={() => {
                    projectStore.setShowProjectDefaultsModal(true);
                  }}
                >
                  {`Value defaults to ${projectStore.getDefaultAverageDailyTraffic(
                    form.type,
                  )} for road type "${t(`shared:hlaWayType.${form.type}`)}"`}
                </Link>
              )}
            </FieldWrapper>

            <FieldWrapper label={t(`shared:hlaAttribute.lit`)}>
              <Checkbox.Root
                disabled={!canEdit}
                colorPalette="primary"
                checked={form.lit}
                onCheckedChange={({ checked }) => {
                  hlaStore.code((prev) => ({
                    ...prev,
                    lit: checked as boolean,
                  }));
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
            </FieldWrapper>
          </SimpleGrid>
        </Stack>

        <Stack>
          <Separator />
          <Text textStyle={"lg"} px={6}>
            Bike lanes
          </Text>
        </Stack>

        <HStack
          justifyContent={"center"}
          flexDir={projectStore.rightHandTraffic ? "row" : "row-reverse"}
        >
          {form.backFacingLane ? (
            <CrossSectionView
              form={form}
              lane={form.backFacingLane}
              canEdit={canEdit}
            />
          ) : (
            <NewLaneButton frontFacing={false} canEdit={canEdit} />
          )}
          {form.frontFacingLane ? (
            <CrossSectionView
              form={form}
              lane={form.frontFacingLane}
              canEdit={canEdit}
            />
          ) : (
            <NewLaneButton frontFacing={true} canEdit={canEdit} />
          )}
        </HStack>

        {!!selectedLane && <HlaLaneForm lane={selectedLane} />}
      </Stack>

      {cutHlaModal.open && <CutHlaModal onClose={cutHlaModal.onClose} />}
    </Stack>
  );
};

export const HlaWayDetail = observer(HlaWayDetailBase);
